import axios from 'axios';
import { ProgramRequest, ScheduleRequest, Program } from '@finpay-payment-v3/payment-types';
import { getPaymentApiBaseUrl, getPaymentApiPathPrefix } from './api-config';

/** Strip response-only fields from schedule so API accepts the payload (e.g. pfrPendingBalance is not allowed in body) */
function scheduleForApi(schedule: ScheduleRequest | undefined): ScheduleRequest | undefined {
  if (!schedule) return undefined;
  const { pfrPendingBalance: _omit, ...rest } = schedule as ScheduleRequest & { pfrPendingBalance?: number };
  return rest as ScheduleRequest;
}

export interface CreateProgramParams extends Partial<ProgramRequest> {
  clientId: number;
  facilityId: number;
  patientId: number;
  patientEncounterId: number;
  programPfrAmt: number;
  patientPaymentSchedule?: ScheduleRequest;
  authToken?: string;
}

export interface CreateProgramResult {
  success: boolean;
  programId?: number;
  program?: Program;
  message: string;
}

export const createProgram = async (params: CreateProgramParams): Promise<CreateProgramResult> => {
  const { authToken, ...programData } = params;
  
  try {
    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/program`;
    
    const payload = {
      clientId: programData.clientId,
      facilityId: programData.facilityId,
      patientId: programData.patientId,
      patientEncounterId: programData.patientEncounterId,
      isHighRisk: programData.isHighRisk ?? false,
      isPaidInFull: programData.isPaidInFull ?? false,
      programPfrAmt: programData.programPfrAmt,
      downPmtAmt: programData.downPmtAmt,
      isDownPmtAuthorized: programData.isDownPmtAuthorized ?? false,
      isDownPmtCaptured: programData.isDownPmtCaptured ?? false,
      isAch: programData.isAch ?? false,
      isAchVerified: programData.isAchVerified ?? false,
      areDocsSigned: programData.areDocsSigned ?? false,
      ...(programData.patientPaymentSchedule && { patientPaymentSchedule: scheduleForApi(programData.patientPaymentSchedule) }),
    };
    
    const response = await axios.post(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && {
          'Authorization': `Bearer ${authToken}`,
          'x-cog-id': authToken,
        }),
      },
    });
    
    if (response.status === 200 || response.status === 201) {
      const createdProgram = response.data;
      return {
        success: true,
        programId: createdProgram.patientPaymentProgramId,
        program: createdProgram,
        message: 'Program created successfully.',
      };
    }
    
    return {
      success: false,
      message: 'Unexpected response status: ' + response.status,
    };
  } catch (err: any) {
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred creating program.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

export interface UpdateProgramParams {
  programId: number;
  downPmtAmt?: number;
  patientPaymentSchedule?: ScheduleRequest;
  isAch?: boolean;
  isAchVerified?: boolean;
  isPaidInFull?: boolean;
  authToken?: string;
}

export interface UpdateProgramResult {
  success: boolean;
  program?: Program;
  message: string;
}

export const updateProgram = async (params: UpdateProgramParams): Promise<UpdateProgramResult> => {
  const { programId, downPmtAmt, patientPaymentSchedule, isAch, isAchVerified, isPaidInFull, authToken } = params;

  try {
    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/program/${programId}`;

    const payload: { downPmtAmt?: number; patientPaymentSchedule?: ScheduleRequest; isAch?: boolean; isAchVerified?: boolean; isPaidInFull?: boolean } = {};
    if (downPmtAmt !== undefined) payload.downPmtAmt = downPmtAmt;
    if (patientPaymentSchedule) payload.patientPaymentSchedule = scheduleForApi(patientPaymentSchedule);
    if (isAch !== undefined) payload.isAch = isAch;
    if (isAchVerified !== undefined) payload.isAchVerified = isAchVerified;
    if (isPaidInFull !== undefined) payload.isPaidInFull = isPaidInFull;

    const response = await axios.patch(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && {
          Authorization: `Bearer ${authToken}`,
          'x-cog-id': authToken,
        }),
      },
    });

    if (response.status === 200) {
      return {
        success: true,
        program: response.data,
        message: 'Program updated successfully.',
      };
    }

    return {
      success: false,
      message: 'Unexpected response status: ' + response.status,
    };
  } catch (err: any) {
    const responseData = err.response?.data;
    const errorMessage =
      responseData?.externalResponse?.message ||
      responseData?.message ||
      err.message ||
      'An unexpected error occurred updating program.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

export interface UpdateProgramScheduleParams {
  programId: number;
  patientEncounterId: number;
  recurringUserPaymentMethodId?: number;
  nextPaymentDueDt?: string;
  isPaidInFull?: boolean;
  isAch?: boolean;
  isAchVerified?: boolean;
  userEmail?: string;
  authToken?: string;
}

export interface UpdateProgramScheduleResult {
  success: boolean;
  program?: Program;
  message: string;
}

export const updateProgramSchedule = async (params: UpdateProgramScheduleParams): Promise<UpdateProgramScheduleResult> => {
  const { programId, patientEncounterId, recurringUserPaymentMethodId, nextPaymentDueDt, isPaidInFull, isAch, isAchVerified, authToken } = params;
  
  const paymentStartDay = nextPaymentDueDt ? new Date(nextPaymentDueDt).getDate() : undefined;
  
  try {
    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/program/${programId}`;
    
    const payload = {
      patientEncounterId,
      // Only include isPaidInFull in payload if it's explicitly true
      ...(isPaidInFull === true && { isPaidInFull: true }),
      // Include ACH flags when provided
      ...(isAch !== undefined && { isAch }),
      ...(isAchVerified !== undefined && { isAchVerified }),
      patientPaymentSchedule: {
        ...(recurringUserPaymentMethodId && { recurringUserPaymentMethodId }),
        scheduleStatus: 'active',
        ...(nextPaymentDueDt && { nextPaymentDueDt, paymentStartDay }),
      },
    };
    
    const response = await axios.patch(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && {
          'Authorization': `Bearer ${authToken}`,
          'x-cog-id': authToken,
        }),
      },
    });
    
    return {
      success: true,
      program: response.data, // Return the updated program from PATCH response
      message: 'Schedule updated successfully.',
    };
  } catch (err: any) {
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred updating program schedule.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

export interface FetchProgramParams {
  programId: number;
  authToken?: string;
}

export interface FetchProgramResult {
  success: boolean;
  program?: Program;
  message: string;
}

export const fetchProgram = async (params: FetchProgramParams): Promise<FetchProgramResult> => {
  const { programId, authToken } = params;
  
  try {
    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/program/${programId}`;
    
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && {
          'Authorization': `Bearer ${authToken}`,
          'x-cog-id': authToken,
        }),
      },
    });
    
    if (response.status === 200) {
      return {
        success: true,
        program: response.data,
        message: 'Program fetched successfully.',
      };
    }
    
    return {
      success: false,
      message: 'Unexpected response status: ' + response.status,
    };
  } catch (err: any) {
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred fetching program.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};
